import React, { useState } from 'react';
import { formatDateToCompanyFormat } from '../../utils/dateUtils';
import QuotationBillShell from './shared/QuotationBillShell';
import { useQuotationBillData } from './shared/useQuotationBillData';
import QuotationSubPartyBreakdown from './shared/QuotationSubPartyBreakdown';
import QuotationAmountToggle from './shared/QuotationAmountToggle';
import QuotationItemsTable from './shared/QuotationItemsTable';
import QuotationBankTermsFooter from './shared/QuotationBankTermsFooter';

function QuotationTemplateColored({
  billData = {},
  companyDetails = {},
  partyDetails = {},
  bankDetails: _bankDetails = {},
  showAmountsByDefault = false,
  previewMode = false,
  sharePreview = false,
  pageSize = 'a4',
  orientation = 'portrait',
}) {
  const [showAmounts, setShowAmounts] = useState(showAmountsByDefault);
  const screenPreviewBoost = previewMode || sharePreview;
  const {
    displayItems,
    subPartyBreakdownList,
    totalBeforeDiscount,
    discount,
    payableAmount,
    terms,
    sparseLayout,
    hasLineDiscount,
    discountLabel,
    hasSubPartyColumn,
  } = useQuotationBillData(billData, companyDetails);

  return (
    <QuotationBillShell
      pageSize={pageSize}
      orientation={orientation}
      screenPreviewBoost={screenPreviewBoost}
      sparseLayout={sparseLayout}
    >
      <div className="quotation-doc-header">
        <div className="relative bg-gradient-to-r from-sky-50 via-white to-indigo-50/80 text-gray-800 p-4 sm:p-6 rounded-t-lg mb-6 border-b border-sky-100 print:bg-gradient-to-r print:from-sky-50 print:via-white print:to-indigo-50">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              {companyDetails.logoUrl && (
                <div className="bg-white p-2 rounded-lg shadow-sm border border-gray-200">
                  <img src={companyDetails.logoUrl} alt="Logo" className="h-14 w-14 object-contain" />
                </div>
              )}
              <div>
                <div className="text-2xl font-bold uppercase text-slate-800">{companyDetails.firmName || ''}</div>
                <div className="text-gray-700 text-sm mt-1">
                  {companyDetails.address || ''} {companyDetails.city || ''} {companyDetails.pincode || ''}
                </div>
                {companyDetails.gstin && (
                  <div className="text-gray-600 text-xs mt-1">GSTIN: {companyDetails.gstin}</div>
                )}
                {companyDetails.contactNumber && (
                  <div className="text-gray-600 text-xs">Contact: {companyDetails.contactNumber}</div>
                )}
              </div>
            </div>
            <div className="text-left sm:text-right bg-white/80 px-3 sm:px-4 py-2 rounded-lg border border-sky-100 shadow-sm w-full sm:w-auto shrink-0">
              <div className="text-2xl sm:text-3xl font-bold tracking-wider text-sky-700">QUOTATION</div>
              <div className="mt-2 text-gray-700">
                <div className="text-sm">
                  <span className="font-semibold">Quotation No:</span>{' '}
                  {billData.quotationNumber || billData.invoiceNumber || billData.number || ''}
                </div>
                <div className="text-sm">
                  <span className="font-semibold">Date:</span>{' '}
                  {formatDateToCompanyFormat(billData.date || billData.invoiceDate, companyDetails)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="border-l-4 border-sky-200 bg-sky-50/50 p-4 rounded-r-lg">
            <div className="font-bold text-sky-600 mb-2 uppercase text-xs">From</div>
            <div className="font-semibold text-gray-900">{companyDetails.firmName || ''}</div>
            <div className="text-sm text-gray-700 mt-1">{companyDetails.address || ''}</div>
            <div className="text-sm text-gray-700">
              {companyDetails.city || ''} {companyDetails.pincode || ''}
            </div>
            <div className="text-sm text-gray-700">GSTIN: {companyDetails.gstin || 'N/A'}</div>
            <div className="text-sm text-gray-700">Contact: {companyDetails.contactNumber || ''}</div>
          </div>

          <div className="border-l-4 border-indigo-200 bg-indigo-50/50 p-4 rounded-r-lg">
            <div className="font-bold text-indigo-600 mb-2 uppercase text-xs">To</div>
            <div className="font-semibold text-gray-900">{partyDetails.firmName || partyDetails.name || ''}</div>
            <div className="text-sm text-gray-700 mt-1">{partyDetails.address || ''}</div>
            <div className="text-sm text-gray-700">
              {partyDetails.city || ''} {partyDetails.pincode || ''}
            </div>
            <div className="text-sm text-gray-700">GSTIN: {partyDetails.gstin || 'N/A'}</div>
            <div className="text-sm text-gray-700">Contact: {partyDetails.contactNumber || ''}</div>
          </div>
        </div>

        {billData.notes && (
          <div className="bg-amber-50/60 border-l-4 border-amber-200 p-3 rounded-r mb-4">
            <div className="font-semibold text-amber-700 mb-1">Note:</div>
            <div className="italic text-amber-600/90 text-sm">{billData.notes}</div>
          </div>
        )}

        <QuotationSubPartyBreakdown
          breakdown={subPartyBreakdownList}
          billData={billData}
          payableAmount={payableAmount}
          variant="colored"
        />

        <QuotationAmountToggle
          showAmounts={showAmounts}
          onToggle={() => setShowAmounts(!showAmounts)}
          variant="colored"
        />
      </div>

      <QuotationItemsTable
        variant="colored"
        displayItems={displayItems}
        showAmounts={showAmounts}
        totalBeforeDiscount={totalBeforeDiscount}
        discount={discount}
        payableAmount={payableAmount}
        hasLineDiscount={hasLineDiscount}
        discountLabel={discountLabel}
        hasSubPartyColumn={hasSubPartyColumn}
      />

      <QuotationBankTermsFooter companyDetails={companyDetails} terms={terms} variant="colored" />
    </QuotationBillShell>
  );
}

export default QuotationTemplateColored;
